import { format } from "date-fns";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { PreconCover, PreconRevision, PreconSignatory } from "./precon-types";

interface Props {
  cover: PreconCover;
  onCoverChange: (next: PreconCover) => void;
  headerBanner: string;
  onHeaderBannerChange: (next: string) => void;
  /**
   * The project's logo URL, or null when none is set. Shown top-right exactly
   * where it prints; when absent a dashed placeholder notes the Otus fallback
   * the generated PDF uses. Managed in settings — see
   * [[project-logo-section.spec]].
   */
  logo: string | null;
}

const BLANK_REVISION: PreconRevision = { rev: "", description: "", date: "" };

type SignatoryKey = "prepared_by" | "approved_by";

const SIGNATORY_LABEL: Record<SignatoryKey, string> = {
  prepared_by: "Prepared by",
  approved_by: "Approved by",
};

/**
 * The name + designation inputs for one signatory. Rendered twice per
 * signatory — once in its md+ table column ("column") and once in the <md
 * compact pair beneath the revision rows ("compact"); CSS shows exactly one
 * arrangement per breakpoint, and both bind the same cover state. The
 * accessible names differ between variants ("Prepared by name" vs
 * "Prepared by — name") so each stays uniquely addressable, and only the
 * column variant carries the stable `{kind}-name` / `{kind}-desig` ids.
 */
function SignatoryFields({
  kind,
  variant,
  value,
  onPatch,
}: {
  kind: SignatoryKey;
  variant: "column" | "compact";
  value?: PreconSignatory;
  onPatch: (kind: SignatoryKey, patch: Partial<PreconSignatory>) => void;
}) {
  const label = SIGNATORY_LABEL[kind];
  const isColumn = variant === "column";
  return (
    <div className="space-y-1.5">
      {!isColumn && (
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
      )}
      <Input
        id={isColumn ? `${kind}-name` : undefined}
        aria-label={isColumn ? `${label} name` : `${label} — name`}
        placeholder="Name"
        value={value?.name ?? ""}
        onChange={(e) => onPatch(kind, { name: e.target.value })}
        className="h-8 text-sm"
      />
      <Input
        id={isColumn ? `${kind}-desig` : undefined}
        aria-label={isColumn ? `${label} designation` : `${label} — designation`}
        placeholder="Designation"
        value={value?.designation ?? ""}
        onChange={(e) => onPatch(kind, { designation: e.target.value })}
        className="h-8 text-sm"
      />
    </div>
  );
}

/**
 * Read-only repeat of a signatory on revision rows after the first — the PDF
 * prints the same pair beside every row, so the table shows that without
 * offering a second editing surface.
 */
function SignatoryEcho({ value }: { value?: PreconSignatory }) {
  const name = value?.name.trim();
  const designation = value?.designation.trim();
  if (!name && !designation) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="space-y-0.5 text-xs text-muted-foreground">
      <p>{name || "—"}</p>
      {designation ? <p>{designation}</p> : null}
    </div>
  );
}

export function PreconCoverStep({
  cover,
  onCoverChange,
  headerBanner,
  onHeaderBannerChange,
  logo,
}: Props) {
  const set = (patch: Partial<PreconCover>) =>
    onCoverChange({ ...cover, ...patch });
  const setSignatory = (key: SignatoryKey, patch: Partial<PreconSignatory>) =>
    set({ [key]: { name: "", designation: "", ...cover[key], ...patch } });
  const setRevision = (i: number, patch: Partial<PreconRevision>) =>
    set({
      revisions: cover.revisions.map((r, j) =>
        j === i ? { ...r, ...patch } : r,
      ),
    });

  const hasRevisions = cover.revisions.length > 0;

  /** One per-revision-row cell of the signatory columns (md+ only). */
  const signatoryCell = (kind: SignatoryKey, rowIndex: number) => (
    <td className="hidden px-2 py-2 align-top md:table-cell">
      {rowIndex === 0 ? (
        <SignatoryFields
          kind={kind}
          variant="column"
          value={cover[kind]}
          onPatch={setSignatory}
        />
      ) : (
        <SignatoryEcho value={cover[kind]} />
      )}
    </td>
  );

  return (
    <section className="space-y-4">
      <h2 className="text-base font-semibold">Cover</h2>

      {/* The page sheet: fields sit where they print, so the layout itself
          answers "which part of the report is this field for?" (see
          [[precon-report-builder.spec]] Layout § Form-as-document). */}
      <div className="mx-auto w-full max-w-3xl rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:p-6">
        {/* Print header row: banner strip top-center, project logo top-right. */}
        <div className="flex items-start gap-4">
          {/* Invisible twin of the logo slot so the banner centres on the
              sheet exactly as it prints; dropped on phones where the width
              is better spent on the input. */}
          <div aria-hidden className="hidden w-20 shrink-0 sm:block" />
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor="banner" className="sr-only">
              Running header banner
            </Label>
            <Input
              id="banner"
              value={headerBanner}
              onChange={(e) => onHeaderBannerChange(e.target.value)}
              placeholder="Running header banner"
              className="h-8 text-center text-sm"
            />
            <p className="text-center text-xs text-muted-foreground">
              Prints at the top of every page
            </p>
          </div>
          {/* Project-logo slot — display only. The builder never uploads a
              logo; it shows the project's own logo when set (managed in
              settings), and a dashed placeholder noting the Otus fallback the
              generated PDF uses when the project has none. */}
          {logo ? (
            <img
              src={logo}
              alt="Project logo"
              className="h-12 w-20 shrink-0 rounded-md border object-contain p-1"
            />
          ) : (
            <div className="flex h-12 w-20 shrink-0 flex-col items-center justify-center rounded-md border border-dashed text-[9px] leading-tight font-medium tracking-wide text-muted-foreground uppercase">
              Logo
              <span className="mt-0.5 normal-case opacity-70">Otus fallback</span>
            </div>
          )}
        </div>

        <div className="mt-8 space-y-1.5">
          <Label
            htmlFor="title"
            className="block text-center text-xs font-normal text-muted-foreground"
          >
            Title
          </Label>
          <Input
            id="title"
            value={cover.title}
            onChange={(e) => set({ title: e.target.value })}
            className="h-12 text-center text-xl font-semibold md:text-xl"
          />
        </div>

        <div className="mt-6 space-y-1.5">
          <Label
            htmlFor="desc"
            className="text-xs font-normal text-muted-foreground"
          >
            Project description
          </Label>
          <Textarea
            id="desc"
            value={cover.project_description}
            onChange={(e) => set({ project_description: e.target.value })}
            rows={3}
          />
        </div>

        <div className="mt-6 space-y-1.5">
          <Label
            htmlFor="subtitle"
            className="block text-center text-xs font-normal text-muted-foreground"
          >
            Report subtitle
          </Label>
          <Input
            id="subtitle"
            value={cover.report_subtitle}
            onChange={(e) => set({ report_subtitle: e.target.value })}
            className="text-center"
          />
        </div>

        {/* Merged sign-off table — revisions as rows, signatories in their
            columns, exactly as the cover prints. On <md the signatory columns
            compress out and the pair is edited once beneath the rows (the
            tfoot), still inside the one table so the join stays visible. */}
        <div className="mt-8 space-y-3">
          <table
            aria-label="Sign-off table"
            className="w-full border-collapse text-sm"
          >
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th scope="col" className="w-14 px-2 pb-1.5 font-medium">
                  Rev
                </th>
                <th scope="col" className="px-2 pb-1.5 font-medium">
                  Description
                </th>
                <th
                  scope="col"
                  className="w-32 px-2 pb-1.5 font-medium md:w-36"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="hidden w-40 px-2 pb-1.5 font-medium md:table-cell"
                >
                  Prepared by
                </th>
                <th
                  scope="col"
                  className="hidden w-40 px-2 pb-1.5 font-medium md:table-cell"
                >
                  Approved by
                </th>
                <th scope="col" className="w-9 pb-1.5">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {!hasRevisions ? (
                // No revisions yet: the signatories still need a home in
                // their columns, so a single placeholder row keeps them
                // editable (and the table join visible) until a row is added.
                <tr className="border-b align-top">
                  <td
                    colSpan={3}
                    className="px-2 py-3 text-xs text-muted-foreground italic"
                  >
                    No revisions yet
                  </td>
                  {signatoryCell("prepared_by", 0)}
                  {signatoryCell("approved_by", 0)}
                  <td />
                </tr>
              ) : (
                cover.revisions.map((r, i) => (
                  <tr key={i} className="border-b align-top">
                    <td className="px-2 py-2">
                      <Input
                        aria-label="Rev"
                        value={r.rev}
                        onChange={(e) =>
                          setRevision(i, { rev: e.target.value })
                        }
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        aria-label="Description"
                        value={r.description}
                        onChange={(e) =>
                          setRevision(i, { description: e.target.value })
                        }
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        aria-label="Date"
                        type="date"
                        value={r.date}
                        onChange={(e) =>
                          setRevision(i, { date: e.target.value })
                        }
                        className="h-8 text-sm"
                      />
                    </td>
                    {signatoryCell("prepared_by", i)}
                    {signatoryCell("approved_by", i)}
                    <td className="py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove revision"
                        onClick={() =>
                          set({
                            revisions: cover.revisions.filter(
                              (_, j) => j !== i,
                            ),
                          })
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {/* <md compression: the signatory pair is edited once here. The
                printed table still repeats it beside every revision row. */}
            <tfoot className="md:hidden">
              <tr>
                <td colSpan={6} className="px-2 pt-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Prepared by / Approved by — print beside every revision row
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <SignatoryFields
                      kind="prepared_by"
                      variant="compact"
                      value={cover.prepared_by}
                      onPatch={setSignatory}
                    />
                    <SignatoryFields
                      kind="approved_by"
                      variant="compact"
                      value={cover.approved_by}
                      onPatch={setSignatory}
                    />
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {!hasRevisions && (
              <>
                <p className="text-xs text-muted-foreground">
                  The sign-off table prints bare without a revision.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    set({
                      revisions: [
                        {
                          rev: "0",
                          description: "Issued for survey",
                          date: format(new Date(), "yyyy-MM-dd"),
                        },
                      ],
                    })
                  }
                >
                  Add Rev 0
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                set({ revisions: [...cover.revisions, { ...BLANK_REVISION }] })
              }
            >
              <Plus className="mr-1 h-4 w-4" /> Add revision
            </Button>
          </div>
        </div>

      </div>
    </section>
  );
}
